import { ReactNode, useEffect, useRef, useState } from 'react';

interface Column {
  key: string;
  label: string;
  width?: string; // Now supports: '15%', '1fr', 'minmax(120px, 1fr)', or legacy 'px' values
  minWidth?: string; // Optional minimum width (e.g., '100px', '8rem')
  maxWidth?: string; // Optional maximum width (e.g., '300px', '20rem')
  sticky?: 'left' | 'right'; // Pin this column while scrolling horizontally
  render?: (value: any, row: any) => ReactNode;
}

interface ScrollableTableProps {
  columns: Column[];
  data: any[];
  isLoading?: boolean;
  onLoadMore?: () => void;
  hasMore?: boolean;
  height?: string;
  emptyMessage?: string;
  onRowClick?: (row: any) => void;
  highlightedRowId?: number | string | null;
  getRowRef?: (rowId: number | string) => React.RefObject<HTMLTableRowElement | null> | null;
  align?: 'left' | 'center' | 'right';
}

export default function ScrollableTable({
  columns,
  data,
  isLoading = false,
  onLoadMore,
  hasMore = false,
  height = '600px',
  emptyMessage = 'No data available',
  onRowClick,
  highlightedRowId = null,
  getRowRef,
  align = 'center',
}: ScrollableTableProps) {
  const alignClass =
    align === 'left' ? 'text-left' : align === 'right' ? 'text-right' : 'text-center';
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const lastScrollTopRef = useRef(0);
  const checkTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastLoadTimeRef = useRef<number>(0);
  const MIN_LOAD_INTERVAL = 1000; // Minimum 1 second between loads

  // Check if content fits in container without scrolling, and load more if needed
  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer || !onLoadMore || !hasMore || isLoadingMore) return;

    const checkIfNeedsMoreData = () => {
      if (isLoadingMore) return;
      
      // Throttle: Don't load if we loaded recently
      const now = Date.now();
      if (now - lastLoadTimeRef.current < MIN_LOAD_INTERVAL) {
        return;
      }
      
      const { scrollHeight, clientHeight } = scrollContainer;
      
      // If content height is less than or equal to container height, no scrollbar exists
      if (scrollHeight <= clientHeight && hasMore && !isLoadingMore) {
        lastLoadTimeRef.current = now;
        setIsLoadingMore(true);
        onLoadMore();
      }
    };

    // Clear any existing timeout
    if (checkTimeoutRef.current) {
      clearTimeout(checkTimeoutRef.current);
    }

    // Check after a delay to ensure DOM is fully rendered
    checkTimeoutRef.current = setTimeout(checkIfNeedsMoreData, 300);

    return () => {
      if (checkTimeoutRef.current) {
        clearTimeout(checkTimeoutRef.current);
      }
    };
  }, [data.length, onLoadMore, hasMore, isLoadingMore]);

  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer || !onLoadMore || !hasMore) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = scrollContainer;
      
      // Only trigger load more on VERTICAL scroll
      if (scrollTop === lastScrollTopRef.current) {
        return;
      }
      lastScrollTopRef.current = scrollTop;
      
      // Throttle: Don't load if we loaded recently
      const now = Date.now();
      if (now - lastLoadTimeRef.current < MIN_LOAD_INTERVAL) {
        return;
      }
      
      const scrollPercentage = (scrollTop + clientHeight) / scrollHeight;

      // Load more when scrolled to 80% of the container vertically
      if (scrollPercentage > 0.8 && !isLoadingMore && hasMore) {
        lastLoadTimeRef.current = now;
        setIsLoadingMore(true);
        onLoadMore();
      }
    };

    scrollContainer.addEventListener('scroll', handleScroll, { passive: true });
    return () => scrollContainer.removeEventListener('scroll', handleScroll);
  }, [onLoadMore, hasMore, isLoadingMore]);

  useEffect(() => {
    if (!isLoading) {
      setIsLoadingMore(false);
    }
  }, [isLoading]);

  if (isLoading && data.length === 0) {
    return (
      <div className="flex items-center justify-center" style={{ height }}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading data...</p>
        </div>
      </div>
    );
  }

  if (!isLoading && data.length === 0) {
    return (
      <div className="flex items-center justify-center" style={{ height }}>
        <div className="text-center">
          <svg className="w-16 h-16 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
          </svg>
          <p className="text-gray-600">{emptyMessage}</p>
        </div>
      </div>
    );
  }

  // Calculate column styles with flexible widths
  const getColumnStyle = (column: Column) => {
    const style: React.CSSProperties = {};
    
    // If width is provided, use it directly (supports %, fr, px, etc.)
    if (column.width) {
      style.width = column.width;
      // For pixel values, also set minWidth to maintain consistency
      if (column.width.endsWith('px')) {
        style.minWidth = column.width;
      }
    } else {
      // Default: flexible width with reasonable minimum
      style.minWidth = '100px';
      style.width = 'auto';
    }
    
    // Apply optional min/max widths (these override defaults)
    if (column.minWidth) {
      style.minWidth = column.minWidth;
    }
    if (column.maxWidth) {
      style.maxWidth = column.maxWidth;
    }
    
    return style;
  };

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden w-full">
      {/* Scrollable Container - both horizontal and vertical */}
      <div
        ref={scrollContainerRef}
        className="overflow-auto bg-white"
        style={{ height, width: '100%' }}
      >
        <table className="w-full divide-y divide-gray-200" style={{ tableLayout: 'auto' }}>
          {/* Fixed Header */}
          <thead className="bg-gradient-to-r from-gray-100 to-gray-50 sticky top-0 z-10">
            <tr>
              {columns.map((column) => (
                <th
                  key={column.key}
                  className={`px-4 py-2 ${alignClass} text-xs font-medium text-gray-500 uppercase tracking-wide ${
                    column.sticky === 'right'
                      ? 'sticky right-0 z-20 bg-gray-100 shadow-[-6px_0_6px_-4px_rgba(0,0,0,0.08)]'
                      : column.sticky === 'left'
                        ? 'sticky left-0 z-20 bg-gray-100 shadow-[6px_0_6px_-4px_rgba(0,0,0,0.08)]'
                        : ''
                  }`}
                  style={getColumnStyle(column)}
                >
                  {column.label}
                </th>
              ))}
            </tr>
          </thead>

          {/* Body */}
          <tbody className="bg-white divide-y divide-gray-200">
            {data.map((row, rowIndex) => {
              const isHighlighted = highlightedRowId !== null && row.id === highlightedRowId;
              const rowRef = getRowRef ? getRowRef(row.id) : null;
              
              return (
                <tr
                  key={row.id || rowIndex}
                  ref={rowRef as any}
                  className={`transition-all duration-300 border-b border-gray-200 last:border-b-0 group ${
                    isHighlighted 
                      ? 'bg-yellow-100 border-l-4 border-yellow-500 shadow-lg scale-[1.02]' 
                      : 'hover:bg-gradient-to-r hover:from-brand-50 hover:to-transparent hover:shadow-md hover:scale-[1.01]'
                  } ${onRowClick ? 'cursor-pointer' : ''}`}
                  onClick={() => onRowClick && onRowClick(row)}
                >
                {columns.map((column) => (
                  <td
                    key={column.key}
                    className={`${align === 'center' ? 'px-2' : 'px-4'} py-2 text-sm text-gray-900 ${alignClass} ${
                      column.sticky === 'right'
                        ? 'sticky right-0 z-[5] bg-white shadow-[-6px_0_6px_-4px_rgba(0,0,0,0.08)]'
                        : column.sticky === 'left'
                          ? 'sticky left-0 z-[5] bg-white shadow-[6px_0_6px_-4px_rgba(0,0,0,0.08)]'
                          : ''
                    }`}
                    style={getColumnStyle(column)}
                  >
                    {column.render ? (
                      column.render(row[column.key], row)
                    ) : (
                      <div className={`truncate ${alignClass}`} title={typeof row[column.key] === 'string' ? row[column.key] : ''}>
                        {row[column.key] || '-'}
                      </div>
                    )}
                  </td>
                ))}
              </tr>
              );
            })}
          </tbody>
        </table>

        {/* Loading More Indicator */}
        {isLoadingMore && hasMore && (
          <div className="flex items-center justify-center py-4 bg-white">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-brand-600"></div>
            <span className="ml-2 text-sm text-gray-600">Loading more...</span>
          </div>
        )}

        {/* No More Data Indicator */}
        {!hasMore && data.length > 10 && (
          <div className="text-center py-4 text-sm text-gray-500 bg-white">
            No more data to load
          </div>
        )}
      </div>
    </div>
  );
}
